import React, { useEffect, useRef, useState } from "react"
import { navigate } from "gatsby"
import clsx from "clsx"
import FormLanding from "@ecom/ui/components/FormLanding"
import DadataFio from "@ecom/ui/components/FormLanding/fields/DadataFields/DadataFio"
import PhoneField from "@ecom/ui/components/FormLanding/fields/PhoneField"
import AcceptmentField from "@ecom/ui/components/FormLanding/fields/AcceptmentField"
import SubmitButton from "@ecom/ui/components/FormLanding/fields/SubmitButton"
import Container from "@ecom/ui/components/Container"
import SelectField from "@ecom/ui/components/FormLanding/fields/SelectField"
import requiredValidator from "@ecom/ui/components/FormLanding/validators/required"
import formatFieldsToRequest from "./formatFieldsToRequest"
import * as styles from "./formMain.module.scss"

import type { FormMainProps, ValuesFromForm } from "./types"
import { sendToApi, type SendToApiResponse } from "./requests"
import { setManyIDBValues } from "../../utils/idbUtils"
import { DLFormSendEvent, DLGAFormEvent } from "../../helpers/WatcherDL/DLEvents"
import { pushToDL } from "../../helpers/WatcherDL/utils/helper"

const FormIframe = ({
  buttonText = "Отправить заявку",
  hasDecription = false,
  phoneValidate = true,
  title,
  orderNum,
  description,
  formId,
  frame,
  productName,
}: FormMainProps) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)

  const frameRef = useRef<HTMLDivElement>(null)

  // Для Frame
  useEffect(() => {
    if (frame) {
      // Отправить postMessage при инициализаций
      const initialWidth = frameRef.current?.offsetWidth
      const initialHeigth = frameRef.current?.offsetHeight
      const initialMessage = JSON.stringify({
        action: "RESIZE",
        width: `${initialWidth}px`,
        height: `${initialHeigth}px`,
      })
      window.parent.postMessage(initialMessage, "*")

      // Функция для отправки postMessage при изменений размера
      const handleResize = () => {
        const width = frameRef.current?.offsetWidth
        const height = frameRef.current?.offsetHeight
        const resizeMessage = JSON.stringify({
          action: "RESIZE",
          width: `${width}px`,
          height: `${height}px`,
        })
        window.parent.postMessage(resizeMessage, "*")
      }

      window.addEventListener("resize", handleResize)

      return () => {
        window.removeEventListener("resize", handleResize)
      }
    }

    return () => {}
  }, [frame])

  const handleSubmit = (values: ValuesFromForm) => {
    setIsButtonDisabled(true)

    const valuesToRequest = formatFieldsToRequest(values)

    const data = {
      productName,
      ...valuesToRequest,
      phoneValidate,
    }

    sendToApi({ data, path: "/v2/app/public/start" })
      .then(({ requestId, status }: SendToApiResponse) => {
        if (frame) {
          const formSentMessage = JSON.stringify({ action: "FORM_SENT" })
          window.parent.postMessage(formSentMessage, "*")
        }

        setManyIDBValues([
          ["phone", data.phone],
          ["requestId", requestId],
          ["apiId", requestId],
        ])

        switch (status) {
          case "reject":
            DLFormSendEvent({
              result: "error",
              placement: "main",
              reason: "reject",
              eventAction: data.productName,
            })
            navigate("/frame/debet/error-reject")
            break
          case "repeated":
            DLFormSendEvent({
              result: "error",
              placement: "main",
              reason: "repeated",
              eventAction: data.productName,
            })
            navigate("/frame/debet/error-repeat")
            break
          case "error":
            throw Error()
          default:
            DLFormSendEvent({
              result: "success",
              placement: "main",
              reason: "",
              eventAction: data.productName,
            })
            if (frame) {
              if (phoneValidate) {
                pushToDL({
                  event: "smsVerification",
                  result: "loaded",
                  eventLabel: globalThis?.location?.href,
                })
                navigate("/frame/debet/verify")
              } else {
                navigate("/frame/debet/success")
              }
            }
        }
      })
      .catch((error: any) => {
        DLFormSendEvent({
          result: "error",
          placement: "main",
          reason: "reject",
          eventAction: data.productName,
        })
        DLGAFormEvent({
          eventCategory: "server_error",
          eventAction: "error_apply",
        })
        console.error(error)
        navigate("/frame/debet/error-technical")
      })
  }

  const debitCards = [
    {
      product_name: "Дебетовая карта с кэшбэком",
      card_name: "с кэшбэком на категории до 30%",
    },
    {
      product_name: "Дебетовая с кэшбеком на ВСЕ",
      card_name: "с кэшбэком 2% на всё",
    },
    {
      product_name: "Дебетовая карта с % на остаток",
      card_name: "с процентом на остаток",
    },
    {
      product_name: "Дебетовая с кэшбэком на ПДС",
      card_name: "с кэшбэком 3% на ПДС",
    },
  ]

  return (
    <section ref={frameRef} className={clsx(styles.section)} data-exclude={orderNum}>
      <div className={styles.esiaRedesign} />
      <Container className={styles.firstContainer}>
        <div className={styles.wrapper} id={formId}>
          <h2 className={styles.title}>{title}</h2>
          {hasDecription && <p className={styles.description}>{description}</p>}

          <FormLanding name="personalForm" onSubmit={handleSubmit} placement="main">
            <DadataFio className={styles.formField} label="Фамилия Имя Отчество" name="fio" />
            <PhoneField className={styles.formField} name="phone" />
            {!productName && (
              <SelectField
                name="productName"
                validate={requiredValidator}
                classes={{
                  root: styles.select,
                }}
                label="Выберите тип карты"
                items={debitCards.map((i) => ({
                  value: i.product_name,
                  label: i.card_name,
                }))}
              />
            )}
            <AcceptmentField name="agree" />
            <div className={styles.formBtnOuter}>
              <SubmitButton
                className={styles.formBtn}
                color="secondary"
                disabled={isButtonDisabled}
              >
                {buttonText}
              </SubmitButton>
            </div>
          </FormLanding>
        </div>
      </Container>
    </section>
  )
}

export default FormIframe
